import { format, formatDistanceStrict } from 'date-fns'
import { de } from 'date-fns/locale'

import { LOCALSTORAGE_ITEMS, getAivyOsCustomerUrl, sendSlackMessage } from '.'
import { localStorage } from './local-storage'
import {
  contextLine,
  createCooldownStore,
  headlineEmoji,
  stagePrefix
} from './slack-alert-kit'
import { STAGE } from './stage'

export const PAYMENT_ALERT = {
  EXPIRED: 'EXPIRED',
  INVALID_DATES: 'INVALID_DATES',
  NOT_STARTED: 'NOT_STARTED'
}

const COOLDOWN_MS = 24 * 60 * 60 * 1000

// Aivy schreibt seit 2019 Vertraege. Alles davor ist kein Vertragsdatum,
// sondern ein Parse-Unfall: Payment.expires_at ist nullable, new Date(null)
// ergibt 1970 – daraus wurde im #vertrieb "mehr als 56 Jahre abgelaufen".
// Bewusst KEIN Ceiling: "unbefristet" wird gelegentlich als 2099 hinterlegt,
// das ist gewollt und kein Datenfehler.
const PLAUSIBLE_FROM = Date.parse('2019-01-01T00:00:00.000Z')

const parseTimestamp = (value) => {
  const ms = Date.parse(value)

  if (!Number.isFinite(ms) || ms < PLAUSIBLE_FROM) return null

  return ms
}

/**
 * Klassifiziert die Laufzeit eines Payments. Steuert AUSSCHLIESSLICH den
 * Slack-Alert, nie den Zugang – der bleibt in payment-context.js.
 * @returns {'EXPIRED'|'INVALID_DATES'|'NOT_STARTED'|null} null = laeuft.
 */
export const classifyPaymentPeriod = (payment, now = Date.now()) => {
  const startMs = parseTimestamp(payment?.start_at)
  const expiresMs = parseTimestamp(payment?.expires_at)

  // Das Datenproblem geht vor: ohne verwertbares Datum wissen wir NICHT, ob
  // der Vertrag abgelaufen ist – dann darf die Copy das auch nicht behaupten.
  if (startMs === null || expiresMs === null) return PAYMENT_ALERT.INVALID_DATES
  if (now < startMs) return PAYMENT_ALERT.NOT_STARTED
  if (now > expiresMs) return PAYMENT_ALERT.EXPIRED

  return null
}

/**
 * Reine Datenfunktion, laeuft im useMemo. Kein Seiteneffekt.
 * @returns {{type: string, payment: object, partnerName: string, now: number}|null}
 */
export const buildPaymentAlertDescriptor = ({ payment, partner, now }) => {
  const type = classifyPaymentPeriod(payment, now)

  if (!type) return null

  // Abgelaufene Trials sind der Normalfall im Funnel, daraus macht der
  // Vertrieb keinen Alert. Ein Trial mit kaputten Daten schon: Trials
  // verlieren den Zugang wirklich, da wird jemand faelschlich ausgesperrt.
  if (payment.is_trial && type !== PAYMENT_ALERT.INVALID_DATES) return null

  return {
    type,
    payment,
    partnerName: partner?.name || partner?.display_name || 'unbekannt',
    now
  }
}

// --------------------------------------------------------------- Formatierung

const rawValue = (value) => {
  if (value === null) return '`null`'
  if (value === undefined) return '`undefined`'
  if (value === '') return '`""`'

  return `\`${value}\``
}

// Zeigt ein Datum – oder den Rohwert, wenn er nicht parsebar oder unplausibel
// ist. Auf einen unplausiblen Wert wird nie eine Distanz gerechnet.
const asDate = (value) => {
  const ms = parseTimestamp(value)

  return ms === null ? rawValue(value) : format(ms, 'dd.MM.yyyy')
}

// formatDistanceStrict gegen ein explizites `now` statt formatDistanceToNow:
// "vor 14 Tagen" ist im Alert belastbarer als das gerundete "etwa 2 Wochen",
// und mit uebergebenem now sind die Tests deterministisch ohne Fake-Timer.
const asRelative = (value, now) => {
  const ms = parseTimestamp(value)

  if (ms === null) return null

  return formatDistanceStrict(ms, new Date(now), {
    locale: de,
    addSuffix: true
  })
}

// ----------------------------------------------------------------- Copy (DE)

const COPY = {
  [PAYMENT_ALERT.EXPIRED]: ({ partnerName, payment, now }) => ({
    emoji: ':rotating_light:',
    headline: 'Zahlung abgelaufen',
    notification: `Zahlung abgelaufen – ${partnerName} (seit ${asDate(payment.expires_at)})`,
    lead:
      `Die Zahlung von *${partnerName}* ist am ${asDate(payment.expires_at)} ` +
      `ausgelaufen (${asRelative(payment.expires_at, now)}). Der Zugang zum ` +
      'Dashboard bleibt bewusst offen, der Kunde arbeitet also weiter. Bitte ' +
      'prüfe, ob verlängert oder gekündigt wurde, und hinterlege die ' +
      'aktuellen Daten im Aivy OS.'
  }),
  [PAYMENT_ALERT.INVALID_DATES]: ({ partnerName, payment }) => ({
    emoji: ':warning:',
    headline: 'Payment ohne gültige Laufzeit',
    notification: `Payment ohne gültige Laufzeit – ${partnerName}`,
    lead:
      `Beim Payment von *${partnerName}* fehlt eine verwertbare Laufzeit: ` +
      `start_at ist ${rawValue(payment.start_at)}, expires_at ist ` +
      `${rawValue(payment.expires_at)}. Das ist ein *Datenproblem, keine ` +
      'abgelaufene Zahlung* – ob der Vertrag läuft, wissen wir nicht. Bitte ' +
      'trage die Laufzeit im Aivy OS nach.'
  }),
  [PAYMENT_ALERT.NOT_STARTED]: ({ partnerName, payment, now }) => ({
    emoji: ':hourglass_flowing_sand:',
    headline: 'Payment startet erst in der Zukunft',
    notification: `Payment noch nicht gestartet – ${partnerName} (ab ${asDate(payment.start_at)})`,
    lead:
      `Das Payment von *${partnerName}* startet erst am ` +
      `${asDate(payment.start_at)} (${asRelative(payment.start_at, now)}), ` +
      'der Kunde nutzt das Dashboard aber schon jetzt. Bitte prüfe, ob ' +
      'start_at stimmt – sonst zählt die Laufzeit falsch.'
  })
}

const contractField = ({ payment }) =>
  [
    payment.is_trial ? 'Trial' : 'Kunde',
    payment.product_type,
    payment.status,
    typeof payment.seat_count === 'number'
      ? `${payment.seat_count} Seats`
      : null
  ]
    .filter(Boolean)
    .join(' · ')

const periodField = ({ payment }) =>
  `${asDate(payment.start_at)} → ${asDate(payment.expires_at)}`

// -------------------------------------------------------------- Slack-Payload

/**
 * Block-Kit-Payload. `text` ist Pflicht-Fallback: Slack zeigt in
 * Push-Notification, Sidebar und Notification-Preview ausschliesslich `text`,
 * niemals die Blocks.
 */
export const buildPaymentAlertPayload = (descriptor) => {
  const { payment } = descriptor
  const { emoji, headline, notification, lead } =
    COPY[descriptor.type](descriptor)

  return {
    text: `${headlineEmoji(emoji)} ${stagePrefix()}${notification}`,
    // Der Button ist das Klickziel – eine zusaetzliche Unfurl-Karte waere nur
    // Hoehe im Kanal.
    unfurl_links: false,
    unfurl_media: false,
    blocks: [
      {
        // header ist plain_text (max. 150 Zeichen, kein mrkdwn) – die Fettung
        // steht deshalb erst im Section-Block darunter.
        type: 'header',
        text: {
          type: 'plain_text',
          text: `${headlineEmoji(emoji)} ${stagePrefix()}${headline}`,
          emoji: true
        }
      },
      {
        // Vier Felder ergeben in Slack ein 2x2-Raster – scanbar, ohne dass die
        // Karte in eine Spalte umbricht.
        type: 'section',
        fields: [
          { type: 'mrkdwn', text: `*Kunde*\n${descriptor.partnerName}` },
          { type: 'mrkdwn', text: `*Laufzeit*\n${periodField(descriptor)}` },
          { type: 'mrkdwn', text: `*Vertrag*\n${contractField(descriptor)}` },
          { type: 'mrkdwn', text: `*Payment*\n\`${payment.id}\`` }
        ]
      },
      { type: 'section', text: { type: 'mrkdwn', text: lead } },
      {
        // Ein Button statt einer rohen URL: ein eindeutiges Klickziel.
        type: 'actions',
        elements: [
          {
            type: 'button',
            action_id: 'open_aivy_os_customer',
            style: 'primary',
            url: getAivyOsCustomerUrl({ partner_id: payment.partner_id }),
            text: { type: 'plain_text', text: 'Im Aivy OS öffnen', emoji: true }
          }
        ]
      },
      { type: 'context', elements: [{ type: 'mrkdwn', text: contextLine() }] }
    ]
  }
}

// ------------------------------------------------------------- Dedupe (24 h)

// Session-Map als zweite Verteidigungslinie und Pruning stecken im Kit. Der
// Wrapper aus ./local-storage meldet Speicherfehler weiterhin an Bugsnag.
const cooldown = createCooldownStore({
  storageKey: LOCALSTORAGE_ITEMS.PAYMENT_ALERT_COOLDOWN,
  cooldownMs: COOLDOWN_MS,
  storage: localStorage
})

const dedupeKey = ({ type, payment }) =>
  // updatedAt gehoert in den Key: sobald das Payment im Aivy OS angefasst
  // wurde, ist die naechste Meldung neue Information und kein Duplikat.
  [STAGE, payment.partner_id, payment.id, payment.updatedAt, type].join(':')

/**
 * Versendet den Alert – ein Mal pro (Stage, Partner, Payment, Fall) in 24 h.
 * @returns {boolean} true, wenn tatsaechlich gesendet wurde.
 */
export const maybeSendPaymentAlert = (
  descriptor,
  { now = Date.now() } = {}
) => {
  if (!descriptor) return false

  const key = dedupeKey(descriptor)

  if (cooldown.wasSentRecently(key, now)) return false

  // Vor dem Senden markieren, nicht danach: ein fehlgeschlagener Webhook darf
  // keinen Retry-Sturm ausloesen. Den Fehler meldet sendSlackMessage an
  // Bugsnag.
  cooldown.markSent(key, now)

  sendSlackMessage('vertrieb', buildPaymentAlertPayload(descriptor))

  return true
}
